import { HttpErrorResponse, HttpResponseBase } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, Injectable, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as Sentry from '@sentry/angular-ivy';
import { IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { addExceptionMechanism } from '@sentry/utils';
import { ServerError } from 'breeze-client';
import { SaveError } from 'breeze-client/src/entity-manager';
import { environment } from 'environments/environment';
import { first } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { UserService } from '../user/user.service';
import { mapJsonObjectToClient } from './map-json-server-object';
import { TelemetryService } from './telemetry.service';

declare module 'breeze-client' {
  interface HttpResponse {
    response: HttpResponseBase;
  }
}

// Any of these status codes will not result in a Sentry error log
export const knownStatusCodes = [0, 401, 403, 409];

export enum ErrorLevel {
  error = 'Error',
  info = 'Info',
  warning = 'Warning',
}

export interface ErrorDialogData {
  title: string;
  message: string;
  status?: number;
  error?: Error;
  eventId?: string;
}

export type ErrorWarningInfoLike = string | HttpErrorResponse | Error | ServerError;

export const isError = (e: unknown): e is Error => (e as Error).message !== undefined;

export const isServerError = (e: unknown): e is ServerError => (e as ServerError).httpResponse !== undefined;

export const isSaveError = (e: unknown): e is SaveError => (e as SaveError).entityErrors !== undefined;

export const friendlyErrorMessage = (response: HttpErrorResponse): string => {
  if (response.status === 403) {
    return "You don't have permission to perform this action.\n\nPlease contact your administrator if you received this message in error.";
  } else if (response.status === 401) {
    return 'You have been logged out due to inactivity. Click Ok to be taken to the login page.';
  } else {
    return response.message;
  }
};

export const mapHttpResponse = <T extends HttpResponseBase>(response: T): T =>
  mapJsonObjectToClient(response, {
    customizer: (obj, key, value) => {
      // Attempt to parse any JSON string in the response
      if (typeof value === 'string') {
        try {
          return mapJsonObjectToClient(JSON.parse(value));
        } catch (_) {
          return;
        }
      }
    },
  });

export const getErrorInfo = (e: ErrorWarningInfoLike): Omit<ErrorDialogData, 'title'> => {
  if (isServerError(e) && !isSaveError(e)) {
    // Extract HttpErrorResponse
    e = e.httpResponse.response as HttpErrorResponse;
  }

  if (typeof e === 'string') {
    return {
      message: e,
    };
  } else if (e instanceof HttpErrorResponse) {
    const response = mapHttpResponse(e);
    let error: Error;
    if (response.status === 0) {
      error = new Error('Unable to connect to server. Please try again later.');
    } else if (response.error) {
      let errorDetails = response.error;
      if (typeof errorDetails === 'string') {
        errorDetails = { message: errorDetails };
      }
      error = new Error(errorDetails.title || errorDetails.message);
    } else {
      error = new Error(friendlyErrorMessage(response));
    }
    error.name = e.name;
    // additional info for Sentry - see captureException()
    error['transactionName'] = response.url;
    return {
      message: 'Unfortunately, we have hit a snag. Please try again later.',
      status: response.status,
      error,
    };
  } else if (isError(e)) {
    let status: number;
    if (isSaveError(e)) {
      e.message = e.entityErrors
        .map((ee) => ee.errorMessage)
        .filter((value, index, source) => source.indexOf(value) === index)
        .join(', ');
      status = e.httpResponse.status;
    }
    return {
      message: 'Unfortunately, we have hit a snag. Please try again later.',
      status,
      error: e,
    };
  }

  return {
    message: 'An unknown error occured! Please try again later.',
  };
};

/** Return true to ignore event (don't send to Sentry) */
export const sentryIgnore = (event: Sentry.Event, hint: Sentry.EventHint): boolean =>
  // Errors handled by our ErrorHandler have a captureContext; errors handled by sentryWrapped() do not.
  // We want to ignore the errors handled by sentryWrapped() because they have incomplete info.
  !hint.captureContext || !environment.sendToSentry;

@Injectable({
  providedIn: 'root',
})
export class ErrorHandler {
  private dialogIsOpen: boolean;

  constructor(
    public readonly dialog: MatDialog,
    private readonly ngZone: NgZone,
    private telemetryService: TelemetryService, // private readonly analyticsPlugin: IAppInsights
  ) {}

  error(e: ErrorWarningInfoLike): void {
    this.handle(e, ErrorLevel.error);
  }

  warning(e: ErrorWarningInfoLike): void {
    this.handle(e, ErrorLevel.warning);
  }

  info(e: ErrorWarningInfoLike): void {
    this.handle(e, ErrorLevel.info);
  }

  private handle(e: ErrorWarningInfoLike, errorLevel: ErrorLevel): void {
    const dialogData: ErrorDialogData = {
      title: errorLevel,
      ...getErrorInfo(e),
    };

    // Log error to console
    switch (errorLevel) {
      case ErrorLevel.error:
        console.error(dialogData.error || dialogData.message);
        break;
      case ErrorLevel.info:
        // eslint-disable-next-line no-console
        console.info(dialogData.message);
        break;
      case ErrorLevel.warning:
        console.warn(dialogData.message);
        break;
    }

    // Log all unknown status codes to Sentry
    if (knownStatusCodes.indexOf(dialogData.status) === -1) {
      const error = dialogData.error || dialogData;
      error['status'] = dialogData.status;
      dialogData.eventId = this.captureException(error);
    }

    // Log error to application insights
    try {
      const aiException: IExceptionTelemetry = {
        severityLevel: SeverityLevel.Error,
        exception: { name: dialogData.eventId || dialogData.error.name || 'i6ngerror', message: dialogData.message },
      };
      this.telemetryService.logAIException(aiException);
    } catch {
      console.error('LogAIError');
    }
    if (this.dialogIsOpen) {
      return;
    }

    // Show error dialog
    this.ngZone.run(() => {
      this.dialogIsOpen = true;
      const dialogRef = this.dialog.open(ErrorDialogComponent, {
        width: '500px',
        data: dialogData,
      });
      dialogRef.componentInstance.detectChanges();
      dialogRef.afterClosed().subscribe(() => (this.dialogIsOpen = false));
    });
  }

  private captureException(error: unknown): string {
    // Capture handled exception and send it to Sentry.
    return this.ngZone.run(() =>
      Sentry.captureException(error, (scope) => {
        const tx = error['transactionName'];
        if (tx) {
          // transactionName appears as error subtitle in Sentry
          scope.setTransactionName(tx);
        }
        scope.addEventProcessor((event) => {
          addExceptionMechanism(event, {
            type: 'angular',
            handled: false,
          });

          return event;
        });

        return scope;
      }),
    );
  }
}

@Component({
  selector: 'error-dialog',
  templateUrl: './error-dialog-component.html',
})
export class ErrorDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ErrorDialogComponent>,
    private changeDetectorRef: ChangeDetectorRef,
    private authService: AuthService,
    private userService: UserService,
    @Inject(MAT_DIALOG_DATA) public data: ErrorDialogData,
  ) {}

  /** Needed to force data display during template error conditions */
  detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }

  onOk(): void {
    if (this.data.status === 401) {
      // Sign out
      this.authService.signOut();

      // Reload the app
      location.reload();
    } else {
      this.dialogRef.close();

      // In some error cases, dialog close events are not processed
      setTimeout(() => {
        (this.dialogRef as any)._finishDialogClose();
      }, 200);
    }
  }

  onSendErrorReport(): void {
    this.dialogRef.close();

    // In some error cases, dialog close events are not processed
    setTimeout(() => {
      (this.dialogRef as any)._finishDialogClose();
    }, 200);

    this.userService.user$.pipe(first()).subscribe((user) => {
      Sentry.showReportDialog({
        eventId: this.data.eventId,
        user: {
          name: user.name,
          email: user.email,
        },
      });
    });
  }
}
